import React, { useEffect, useRef } from "react";

const BLOBS = [
  { color: [196, 181, 253], a: 0.85, r: 0.85, sx: 0.9, sy: 0.7, px: 0.0, py: 1.4 },
  { color: [233, 213, 255], a: 0.9, r: 0.95, sx: 0.6, sy: 1.1, px: 2.1, py: 0.4 },
  { color: [245, 208, 254], a: 0.75, r: 0.8, sx: 1.2, sy: 0.5, px: 4.2, py: 2.6 },
  { color: [199, 210, 254], a: 0.85, r: 0.9, sx: 0.7, sy: 0.9, px: 1.1, py: 3.9 },
  { color: [251, 207, 232], a: 0.55, r: 0.7, sx: 1.0, sy: 1.3, px: 3.3, py: 5.1 },
  { color: [167, 139, 250], a: 0.4, r: 0.65, sx: 1.4, sy: 0.8, px: 5.4, py: 0.9 },
];

const DARK_BLOBS = [
  { color: [139, 92, 246], a: 0.65, r: 0.95, sx: 0.7, sy: 0.6, px: 0.0, py: 1.4 },
  { color: [162, 28, 175], a: 0.5, r: 0.85, sx: 0.9, sy: 1.0, px: 2.1, py: 0.4 },
  { color: [67, 56, 202], a: 0.6, r: 0.9, sx: 0.6, sy: 0.8, px: 4.2, py: 2.6 },
  { color: [124, 58, 237], a: 0.55, r: 0.8, sx: 1.1, sy: 0.5, px: 1.1, py: 3.9 },
  { color: [217, 70, 239], a: 0.3, r: 0.65, sx: 1.3, sy: 1.1, px: 3.3, py: 5.1 },
];

function GradientMesh({ blobs = BLOBS, base = "#f6f4ff", speed = 0.00065 }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 480;
    const H = 240;
    canvas.width = W;
    canvas.height = H;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;

    const draw = (t) => {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, W, H);
      blobs.forEach((b) => {
        const x = W * (0.5 + 0.48 * Math.sin(t * b.sx + b.px));
        const y = H * (0.5 + 0.46 * Math.cos(t * b.sy + b.py));
        const radius = H * b.r * (1 + 0.22 * Math.sin(t * 0.9 + b.px));
        const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
        const [r, gr, bl] = b.color;
        g.addColorStop(0, `rgba(${r},${gr},${bl},${b.a})`);
        g.addColorStop(1, `rgba(${r},${gr},${bl},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      });
    };

    if (reduce) {
      draw(2.5);
    } else {
      const loop = (ts) => {
        draw(ts * speed);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    return () => cancelAnimationFrame(raf);
  }, [blobs, base, speed]);

  return (
    <canvas
      ref={ref}
      className="w-full h-full"
      style={{ filter: "blur(28px)", transform: "scale(1.15)" }}
    />
  );
}

export default function StripeGradientBand() {
  return (
    <div
      aria-hidden
      className="absolute inset-x-0 -top-48 h-[480px] md:h-[600px] pointer-events-none overflow-hidden"
      style={{ transform: "skewY(-6deg)", transformOrigin: "0 0" }}
      data-testid="hero-gradient-band"
    >
      <GradientMesh />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white/70" />
    </div>
  );
}

export function DarkGradientBackdrop() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="cta-gradient-backdrop">
      <GradientMesh blobs={DARK_BLOBS} base="#4c1d95" speed={0.00045} />
    </div>
  );
}
